/**
 * The Planliste (`MCK-20260812-011`).
 *
 * `column.*` mirrors the **served** column catalogue's keys, not this file's opinion of what
 * the register holds: the server orders the columns and names them, and these are only what
 * each one is called in English. A key the catalogue grows and this file has not is drawn with
 * its key — visibly wrong, which is the right kind of wrong.
 *
 * `slicer.*` is keyed by the **wire field name** the surface descriptor sends, the way the
 * other three rails are. Not `axis.*` — that group is the pivot's on every other screen, and a
 * slice and a group-by are different operations over the same rows.
 */
export const registerEn = {
  'page.title': 'Register',
  'page.subtitle':
    'Every drawing, its current version and where it stands. This is the list you hand over.',

  'export.csv': 'Download CSV',
  'export.pdf': 'Download PDF',
  // The statutory Zoznam dokumentácie (`KLQ-20260817-001`), drawn only on a project under the
  // Slovak statute. The Slovak name is kept: it is the title of a filed document, and an
  // authority looks for that word, not a translation of it.
  'export.skZoznam': 'Download Zoznam dokumentácie (SK)',
  // The same document as the printable PDF (`KLQ-20260929-004`) — the file that is filed.
  'export.skZoznamPdf': 'Download Zoznam dokumentácie (SK) as PDF',

  // The selection's one action (`MCK-20260812-010`): the ticked drawings' current versions go
  // into the issue drawer. Copying a block of rows still works exactly as before.
  'bulk.selected': '{count, plural, one {# drawing selected} other {# drawings selected}}',
  'bulk.clear': 'Clear the selection',
  'bulk.more': 'More actions',
  'bulk.issue': 'Issue',

  'column.identifier': 'Identifier',
  'column.title': 'Title',
  'column.location_path': 'Location',
  'column.document_type': 'Type',
  'column.discipline': 'Discipline',
  'column.phase': 'Phase',
  'column.current_version': 'Version',
  'column.version_date': 'Version date',
  'column.status': 'Status',
  'column.last_issued': 'Last issued',
  'column.last_issued_reason': 'Reason for issue',

  'status.none': 'Not reviewed',
  'status.in_review': 'In review',
  'status.approved': 'Approved',
  'status.approved_with_comments': 'Approved with comments',
  'status.changes_requested': 'Changes requested',

  'filter.status': 'Status',
  'filter.anyStatus': 'Any status',
  'filter.changedAfter': 'Changed since',
  // The hint is not decoration. The obvious reading of "changed" is wrong: a phase advance is
  // not a changed drawing and does not appear, because the delta is version-driven and its
  // next version will surface it.
  'filter.changedAfterHint': 'New drawings, and drawings with a newer version.',

  'group.legend': 'Group by',
  'group.none': 'Nothing',
  'group.phase': 'Phase',
  'group.discipline': 'Discipline',
  'group.documentType': 'Type',
  'group.location': 'Location',
  'group.status': 'Status',
  // Never "None" — that is the answer to *group by what*, and this is a bucket of drawings
  // nobody has filed yet. Those are the ones worth seeing.
  'group.unassigned': 'Not filed yet',

  'slicer.title': 'Axis',
  'slicer.under': 'Location',
  'slicer.objectId': 'Object',
  'slicer.phaseId': 'Phase',
  'slicer.disciplineId': 'Discipline',
  'slicer.documentTypeId': 'Type',
  // Not a value of any vocabulary — the id `axes.ts` turns into `no_phase=true` and
  // its two siblings. It is offered inside each vocabulary facet, last.
  'slicer.unfiled': 'Not filed yet',
  'slicer.noValues': 'Nothing to slice by here.',
  'slicer.unknownValue': 'something this list cannot name',

  'slice.label': 'Current selection',
  'slice.lead': 'Showing',
  'slice.remove': 'Remove from the selection',
  'slice.clear': 'Show everything',

  'change.new': 'New',
  'change.revised': 'Revised',

  'list.explains': 'The register lists the project’s drawings with their current version and state.',
  'list.emptyTitle': 'No drawings yet',
  'list.empty': 'This project has no drawings yet.',
  'list.emptyFilteredTitle': 'Nothing in this selection',
  'list.emptyFiltered': 'No drawing matches this selection.',
  'list.more': 'Show more',
  'list.failed': 'The register could not be loaded.',
  // Stated whenever the server says the answer was bounded. It leaks nothing — a scoped member
  // already sees a pruned tree and knows they are scoped — and its absence is what would be
  // dishonest.
  'list.scoped': 'Filtered to what you may see.',
} as const;
